import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import {
  generateAISkillProtocol,
  generateInfographicLogic,
  generateSkillSlide,
  generateSkillStrategy,
  generateTutorVisual,
} from "../../services/geminiService";
import {
  Plus,
  Trash2,
  Save,
  X,
  Hand,
  Activity,
  ShieldAlert,
  MessageSquare,
  CheckCircle,
  CheckCircle2,
  Info,
  ClipboardList,
  AlertTriangle,
  ListTodo,
  ShieldCheck,
  Settings2,
  Search,
  Sparkles,
  Loader2,
  Download,
  Upload,
  Wand2,
  Play,
  PlayCircle,
  Layers,
  Brain,
  Image as ImageIcon,
  ChevronUp,
  ChevronDown,
  Link as LinkIcon,
  Award,
  Database,
  Target,
  AlertCircle,
  HelpCircle,
  FileText,
  Settings,
  Sliders,
  ExternalLink,
  Maximize2,
  Video,
} from "lucide-react";
import {
  SkillSlide,
  SkillItemSlide,
  SkillSlideType,
  CertificationType,
  BackendQuestion,
  ClinicalSimulatorScenario,
} from "../../types";
import { SkillIconResolver } from "../../data/skillsData.js";
import { StorageService, isSkillHydrated } from "../../services/storageService";
import { PageLoader } from "../../components/PageLoader";
import { useThemeTokens } from "../../hooks/useActiveTheme";
import {
  getSafeEmbedUrl,
  getEmbedSandbox,
  EMBED_ALLOW_POLICY,
  inspectEmbedUrl,
  fetchVideoMetadata,
} from "../../services/embedService";
import { EmbeddedPhasePlayer } from "../../components/EmbeddedPhasePlayer";

const SLIDE_TYPES: {
  type: SkillSlideType;
  label: string;
  icon: any;
  color: string;
}[] = [
  {
    type: "infographic",
    label: "Visual Guide",
    icon: ImageIcon,
    color: "bg-indigo-500",
  },
  {
    type: "overview",
    label: "Significance",
    icon: Info,
    color: "bg-emerald-500",
  },
  {
    type: "supplies",
    label: "Inventory",
    icon: ClipboardList,
    color: "bg-indigo-600",
  },
  {
    type: "procedure",
    label: "Action Step",
    icon: PlayCircle,
    color: "bg-emerald-600",
  },
  {
    type: "criticals",
    label: "Critical Fails",
    icon: ShieldAlert,
    color: "bg-rose-500",
  },
  {
    type: "reporting",
    label: "Communication",
    icon: MessageSquare,
    color: "bg-indigo-400",
  },
  {
    type: "completion",
    label: "Successful Exit",
    icon: CheckCircle,
    color: "bg-emerald-400",
  },
  {
    type: "embedded",
    label: "Embedded Phase",
    icon: LinkIcon,
    color: "bg-indigo-700",
  },
];

export const SkillsContentManager: React.FC = () => {
  const [certId, setCertId] = useState("");
  const [certs, setCerts] = useState<CertificationType[]>([]);
  const [skills, setSkills] = useState<SkillSlide[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<SkillSlide | null>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(-1);
  const [isSaved, setIsSaved] = useState(false);

  const [isBlueprinting, setIsBlueprinting] = useState(false);
  const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);

  const [aiTopic, setAiTopic] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingVisual, setIsGeneratingVisual] = useState(false);
  const [isSynthesizingLogic, setIsSynthesizingLogic] = useState(false);
  const [isGeneratingPhase, setIsGeneratingPhase] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);
  const [activeCollection, setActiveCollection] =
    useState<string>("pending...");
  const [showAiModal, setShowAiModal] = useState(false);
  const [status, setStatus] = useState<{
    type: "error" | "success" | "warning";
    text: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeEditTab, setActiveEditTab] = useState<"phases" | "quiz">(
    "phases",
  );
  const [isLoading, setIsLoading] = useState(true);
  const [simulatorScenarios, setSimulatorScenarios] = useState<ClinicalSimulatorScenario[]>([]);
  const [isAttachingSim, setIsAttachingSim] = useState<boolean>(false);
  const [isExtractingVideoMeta, setIsExtractingVideoMeta] = useState<boolean>(false);
  const [metaNotice, setMetaNotice] = useState<string | null>(null);

  const navigate = useNavigate();
  const theme = useThemeTokens();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExtractVideoMetadata = async (targetUrl?: string) => {
    if (!selectedSkill || activeSlideIndex < 0) return;
    const currentUrl = targetUrl || selectedSkill.slides[activeSlideIndex]?.data?.embedUrl;
    if (!currentUrl) return;
    setIsExtractingVideoMeta(true);
    setMetaNotice(null);
    try {
      const meta = await fetchVideoMetadata(currentUrl);
      if (meta && meta.title) {
        updateSlideData("videoTitle", meta.title);
        if (meta.authorName) updateSlideData("videoAuthor", meta.authorName);
        if (meta.authorUrl) updateSlideData("videoAuthorUrl", meta.authorUrl);
        if (meta.thumbnailUrl) updateSlideData("videoThumbnail", meta.thumbnailUrl);
        if (meta.description) updateSlideData("videoDescription", meta.description);
        const currentData = selectedSkill.slides[activeSlideIndex].data || {};
        if (!currentData.aspectRatio) {
          updateSlideData("aspectRatio", meta.aspectRatio || "16:9");
        }
        const currentTitle = selectedSkill.slides[activeSlideIndex].title || "";
        if (!currentTitle || currentTitle.toLowerCase().includes("embedded") || currentTitle.toLowerCase().includes("chapter")) {
          updateSlideData("title", meta.title);
        }
        setMetaNotice(`Extracted: "${meta.title}" · ${meta.authorName || 'YouTube'}`);
      } else {
        setMetaNotice("No video metadata returned for this URL.");
      }
    } catch (err: any) {
      setMetaNotice("Extraction failed: " + (err.message || "Network error"));
    } finally {
      setIsExtractingVideoMeta(false);
    }
  };

  useEffect(() => {
    StorageService.getCertifications().then((data) => {
      setCerts(data);
      if (data.length > 0) setCertId(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!certId) return;
    const load = async () => {
      setIsLoading(true);
      try {
        // LAZY LOAD: Get summaries for sidebar and simulator scenarios
        const [data, scenarios] = await Promise.all([
          StorageService.getSkillsSummary(certId),
          StorageService.getClinicalSimulators(certId).catch(() => [])
        ]);
        setSkills(data || []);
        setSimulatorScenarios(scenarios || []);
        setActiveCollection(`${certId}_skills`);
        setSelectedSkill(null);
        setActiveSlideIndex(-1);
      } catch (err) {
        console.error("Failed to load skills", err);
        setStatus({ type: "error", text: "Registry Connection Failed" });
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [certId]);

  const handleAttachScenario = async (scenario: ClinicalSimulatorScenario) => {
    if (!selectedSkill) return;
    setIsAttachingSim(true);
    setStatus(null);
    try {
      // 1. Prepare updated skill
      const updatedSkill: SkillSlide = {
        ...selectedSkill,
        simulatorScenarioId: scenario.id,
        hasSimulator: scenario.status !== 'archived',
        isSimulatorEnabled: true,
      };

      // 2. Prepare updated scenario
      const updatedScenario: ClinicalSimulatorScenario = {
        ...scenario,
        skillId: selectedSkill.id,
      };

      // Persist scenario
      await StorageService.saveClinicalSimulator(certId, updatedScenario);

      // Persist skill
      await StorageService.saveSkill(certId, updatedSkill);

      // If another scenario was previously attached to this skill, clear its link
      const prevAttached = simulatorScenarios.find(s => s.id !== scenario.id && (s.skillId === selectedSkill.id || selectedSkill.simulatorScenarioId === s.id));
      if (prevAttached) {
        const cleanedPrev = { ...prevAttached, skillId: '' };
        await StorageService.saveClinicalSimulator(certId, cleanedPrev).catch(() => {});
      }

      // Update state
      setSelectedSkill(updatedSkill);
      setSkills(prev => prev.map(sk => sk.id === updatedSkill.id ? { ...sk, simulatorScenarioId: scenario.id, hasSimulator: scenario.status !== 'archived', isSimulatorEnabled: true } : sk));
      setSimulatorScenarios(prev => prev.map(s => {
        if (s.id === scenario.id) return updatedScenario;
        if (s.skillId === selectedSkill.id) return { ...s, skillId: '' };
        return s;
      }));

      // Broadcast events so other open tabs/views update instantly
      window.dispatchEvent(new CustomEvent('clinical-simulators-updated', {
        detail: { certId, scenario: updatedScenario }
      }));
      window.dispatchEvent(new CustomEvent('skills-updated', {
        detail: { certId, skill: updatedSkill }
      }));

      setStatus({
        type: 'success',
        text: `Clinical Simulator "${scenario.title}" attached to "${selectedSkill.name}". The 360 Sim button is now live for this skill!`,
      });
    } catch (err: any) {
      console.error("Failed to attach simulator:", err);
      setStatus({
        type: 'error',
        text: `Attachment failed: ${err.message || 'Unknown error'}`,
      });
    } finally {
      setIsAttachingSim(false);
    }
  };

  const handleDetachScenario = async () => {
    if (!selectedSkill) return;
    setIsAttachingSim(true);
    setStatus(null);
    try {
      const currentSimId = selectedSkill.simulatorScenarioId;
      const targetSkillId = (selectedSkill.id || '').toLowerCase().trim();

      // 1. Prepare updated skill
      const updatedSkill: SkillSlide = {
        ...selectedSkill,
        simulatorScenarioId: '',
        hasSimulator: false,
        isSimulatorEnabled: false,
      };

      // 2. Persist skill
      await StorageService.saveSkill(certId, updatedSkill);

      // 3. Unlink all matching scenarios
      const matchingScenarios = simulatorScenarios.filter(s => {
        if (!s) return false;
        if (currentSimId && s.id === currentSimId) return true;
        const sSkill = (s.skillId || '').toLowerCase().trim();
        return sSkill === targetSkillId || sSkill === selectedSkill.id;
      });

      for (const targetScenario of matchingScenarios) {
        const unlinkedScenario: ClinicalSimulatorScenario = {
          ...targetScenario,
          skillId: '',
        };
        await StorageService.saveClinicalSimulator(certId, unlinkedScenario);
      }

      // Update state
      setSelectedSkill(updatedSkill);
      setSkills(prev => prev.map(sk => sk.id === updatedSkill.id ? { ...sk, simulatorScenarioId: '', hasSimulator: false, isSimulatorEnabled: false } : sk));
      setSimulatorScenarios(prev => prev.map(s => {
        const sSkill = (s.skillId || '').toLowerCase().trim();
        if ((currentSimId && s.id === currentSimId) || sSkill === targetSkillId || sSkill === selectedSkill.id) {
          return { ...s, skillId: '' };
        }
        return s;
      }));

      // Broadcast events so other open tabs/views update instantly
      window.dispatchEvent(new CustomEvent('clinical-simulators-updated', {
        detail: { certId, skillId: targetSkillId }
      }));
      window.dispatchEvent(new CustomEvent('skills-updated', {
        detail: { certId, skill: updatedSkill }
      }));

      setStatus({
        type: 'success',
        text: `Simulator successfully detached from "${selectedSkill.name}". 360 Sim button is now disabled for this skill.`,
      });
    } catch (err: any) {
      console.error("Failed to detach simulator:", err);
      setStatus({
        type: 'error',
        text: `Detach failed: ${err.message || 'Unknown error'}`,
      });
    } finally {
      setIsAttachingSim(false);
    }
  };

  const handleSelectSkill = async (s: SkillSlide) => {
    setStatus(null);
    setActiveSlideIndex(-1);
    setActiveEditTab("phases");

    // If s is already fully hydrated with real procedure/supplies/content, use it immediately
    if (isSkillHydrated(s)) {
      setSelectedSkill(s);
      return;
    }

    // Hydrate full procedure detail from registry
    setIsHydrating(true);
    setSelectedSkill(s); // Show summary identity immediately while hydrating
    try {
      const full = await StorageService.getSkillById(certId, s.id);
      if (full) {
        setSelectedSkill(full);
        // Cache full data in main list so future selections load instantly
        setSkills((prev) => prev.map((item) => (item.id === s.id ? full : item)));
      }
    } catch (e) {
      console.error("Logic hydration error:", e);
      setStatus({
        type: "error",
        text: "Logic Hydration Failed: Target unreachable.",
      });
    } finally {
      setIsHydrating(false);
    }
  };

  const activeCert = certs.find((c) => c.id === certId);

  const handleSave = async () => {
    if (!certId || !selectedSkill) return;
    setIsSaving(true);
    setStatus(null);

    try {
      // TARGETED SAVE: Only commit the active logic node
      await StorageService.saveSkill(certId, selectedSkill);
      setStatus({
        type: "success",
        text: "Unit logic synchronized to registry.",
      });
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
    } catch (e: any) {
      console.error("Cloud commit failure:", e);
      setStatus({
        type: "error",
        text:
          e.message ||
          "Logic commit failed: High-resolution payloads may exceed connection timeouts.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuggestStrategy = async () => {
    setIsBlueprinting(true);
    setStatus(null);
    try {
      const topics = await generateSkillStrategy(activeCert?.name || "Medical");
      setSuggestedTopics(topics);
      setStatus({
        type: "success",
        text: "AI Strategy Synthesized. Expand topics to Provision.",
      });
    } catch (e) {
      setStatus({ type: "error", text: "Strategy engine interrupted." });
    } finally {
      setIsBlueprinting(false);
    }
  };

  const handleProvisionFromTopic = async (
    topic: string,
    autoBuild: boolean = false,
  ) => {
    if (autoBuild) {
      setAiTopic(topic);
      setShowAiModal(true);
      setStatus(null);
    } else {
      const newSkill: SkillSlide = {
        id: `skill-${Date.now()}`,
        name: topic,
        icon: "Hand",
        slides: [],
        quiz: [],
      };
      setSkills((prev) => [...prev, newSkill]);
      setSelectedSkill(newSkill);
      setStatus({ type: "success", text: `Empty unit provisioned: ${topic}` });
    }
    setSuggestedTopics((prev) => prev.filter((t) => t !== topic));
  };

  const performSkillSynthesis = async (topic: string) => {
    if (!topic.trim()) return;
    setIsGenerating(true);
    setStatus(null);
    try {
      const skill = await generateAISkillProtocol(topic);
      if (!skill || !skill.slides)
        throw new Error("Invalid structure returned from AI.");

      const sanitizedSkill: SkillSlide = {
        ...skill,
        id: `skill-ai-${Date.now()}`,
        slides: skill.slides.map((s: any, idx: number) => ({
          ...s,
          id: `slide-ai-${Date.now()}-${idx}`,
        })),
      };
      setSkills((prev) => [...prev, sanitizedSkill]);
      setSelectedSkill(sanitizedSkill);
      setActiveSlideIndex(-1);
      setStatus({
        type: "success",
        text: "Expert Protocol & Quiz synthesized successfully.",
      });
      return sanitizedSkill;
    } catch (e: any) {
      console.error("AI synthesis error:", e);
      setStatus({
        type: "error",
        text: "AI Synthesis failed: " + (e.message || "Logic bridge error."),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpgradeSelectedSkillWithAI = async () => {
    if (!selectedSkill) return;
    setIsGenerating(true);
    setStatus({
      type: "warning",
      text: `Upgrading "${selectedSkill.name}" with 20+ years Veteran Nurse Educator AI... Please wait.`,
    });
    try {
      const skill = await generateAISkillProtocol(selectedSkill.name);
      if (!skill || !skill.slides)
        throw new Error("Invalid structure returned from AI.");

      const upgradedSkill: SkillSlide = {
        ...selectedSkill,
        ...skill,
        // KEEP THE ORIGINAL IDs to avoid breaking bookmarks/progress!
        id: selectedSkill.id,
        _id: selectedSkill._id,
        slides: skill.slides.map((s: any, idx: number) => ({
          ...s,
          id: s.id || `slide-ai-${Date.now()}-${idx}`,
        })),
      };

      setSelectedSkill(upgradedSkill);
      // Also update in main list
      setSkills((prev) =>
        prev.map((item) =>
          item.id === selectedSkill.id ? upgradedSkill : item,
        ),
      );
      setActiveSlideIndex(-1);
      setStatus({
        type: "success",
        text: `Successfully upgraded "${selectedSkill.name}" with detailed guidelines, exam focus protocols, and nurse rationales! Click Save to write changes.`,
      });
    } catch (e: any) {
      console.error("AI Upgrade failed:", e);
      setStatus({
        type: "error",
        text: "AI Upgrade failed: " + (e.message || "Validation error."),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateAI = async () => {
    const result = await performSkillSynthesis(aiTopic);
    if (result) {
      setShowAiModal(false);
      setAiTopic("");
    }
  };

  const handleExport = () => {
    if (skills.length === 0) return;
    setIsExporting(true);
    try {
      const dataStr = JSON.stringify(skills, null, 2);
      const dataUri =
        "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
      const fileName = `${certId}_procedures_registry_${new Date().toISOString().split("T")[0]}.json`;
      const linkElement = document.createElement("a");
      linkElement.setAttribute("href", dataUri);
      linkElement.setAttribute("download", fileName);
      linkElement.click();
      setStatus({
        type: "success",
        text: "Registry export package generated.",
      });
    } catch (e) {
      setStatus({ type: "error", text: "Export failed." });
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setStatus({ type: "warning", text: "Validating procedure schema..." });

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const data = JSON.parse(text);

        if (!Array.isArray(data))
          throw new Error("Invalid JSON: Root must be an array of Skills.");

        const isValid = data.every((s) => s.name && Array.isArray(s.slides));
        if (!isValid)
          throw new Error(
            "Schema Violation: Some skills lack required phases.",
          );

        setSkills(data);
        setStatus({
          type: "success",
          text: `Successfully staged ${data.length} skills. Click "Save" to commit to registry.`,
        });
      } catch (err: any) {
        setStatus({ type: "error", text: `Ingestion Failed: ${err.message}` });
      } finally {
        setIsImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsText(file);
  };

  const handleAddSkill = () => {
    const newSkill: SkillSlide = {
      id: `skill-${Date.now()}`,
      name: "New Clinical Protocol",
      icon: "Hand",
      slides: [],
      quiz: [],
    };
    setSkills([...skills, newSkill]);
    setSelectedSkill(newSkill);
    setActiveSlideIndex(-1);
    setStatus(null);
  };

  const handleFillPhaseWithAI = async () => {
    if (!selectedSkill || activeSlideIndex < 0) return;
    setIsGeneratingPhase(true);
    setStatus(null);
    try {
      const slide = selectedSkill.slides[activeSlideIndex];
      const content = await generateSkillSlide(
        selectedSkill.name,
        slide.type,
        activeCert?.name || "Nursing",
      );
      const newSlides = [...selectedSkill.slides];
      newSlides[activeSlideIndex] = { ...slide, data: content };
      updateSkillInList({ ...selectedSkill, slides: newSlides });
      setStatus({ type: "success", text: "Phase logic synthesized." });
    } catch (e: any) {
      console.error("Phase Synthesis Error:", e);
      setStatus({ type: "error", text: `Synthesis failed: ${e.message}` });
    } finally {
      setIsGeneratingPhase(false);
    }
  };

  const handleSynthesizeVisualLogic = async () => {
    if (!selectedSkill || activeSlideIndex < 0) return;
    setIsSynthesizingLogic(true);
    try {
      const logic = await generateInfographicLogic(selectedSkill.name);
      if (logic) {
        const newSlides = [...selectedSkill.slides];
        const currentData = newSlides[activeSlideIndex].data || {};
        newSlides[activeSlideIndex] = {
          ...newSlides[activeSlideIndex],
          data: {
            ...currentData,
            visualAlt: logic.visualAlt,
            observations: logic.observations,
          },
        };
        updateSkillInList({ ...selectedSkill, slides: newSlides });
        setStatus({ type: "success", text: "Observation points generated." });
      }
    } catch (e) {
      setStatus({ type: "error", text: "Logic synthesis failed." });
    } finally {
      setIsSynthesizingLogic(false);
    }
  };

  const handleGenerateVisualAnchor = async () => {
    if (!selectedSkill || activeSlideIndex < 0) return;
    const slide = selectedSkill.slides[activeSlideIndex];
    const visualPrompt = slide.data?.visualAlt;

    if (!visualPrompt) {
      setStatus({
        type: "error",
        text: "No visual prompt identified for synthesis.",
      });
      return;
    }

    setIsGeneratingVisual(true);
    try {
      const url = await generateTutorVisual(selectedSkill.name, visualPrompt);
      if (url) {
        updateSlideData("imageUrl", url);
        setStatus({
          type: "success",
          text: "Clinical Illustration Synthesized.",
        });
      }
    } catch (e) {
      setStatus({ type: "error", text: "Visual generation failed." });
    } finally {
      setIsGeneratingVisual(false);
    }
  };

  const addSlide = (type: SkillSlideType) => {
    if (!selectedSkill) return;
    let defaultData = {};
    switch (type) {
      case "overview":
        defaultData = { importance: "", examContext: "", safetyFocus: "" };
        break;
      case "supplies":
        defaultData = { items: [""] };
        break;
      case "procedure":
        defaultData = { steps: [""], visualAlt: "" };
        break;
      case "criticals":
        defaultData = { items: [""] };
        break;
      case "reporting":
        defaultData = { instruction: "", sampleWording: "" };
        break;
      case "completion":
        defaultData = { items: [""] };
        break;
      case "infographic":
        defaultData = { imageUrl: "", visualAlt: "", observations: [""] };
        break;
      case "embedded":
        defaultData = {
          embedMode: "url",
          embedUrl: "",
          embedCode: "",
          embedWidth: "100%",
          embedHeight: "550px",
        };
        break;
    }

    const newSlide: SkillItemSlide = {
      id: `slide-${Date.now()}`,
      type,
      title:
        type === "procedure"
          ? "Phase One"
          : type === "embedded"
            ? "Embedded Phase"
            : undefined,
      data: defaultData,
    };

    const updatedSkill = {
      ...selectedSkill,
      slides: [...(selectedSkill.slides || []), newSlide],
    };
    updateSkillInList(updatedSkill);
    setActiveSlideIndex(updatedSkill.slides.length - 1);
    setActiveEditTab("phases");
  };

  const removeSlide = (idx: number) => {
    if (!selectedSkill || !confirm("Discard this protocol step?")) return;
    const newSlides = selectedSkill.slides.filter((_, i) => i !== idx);
    updateSkillInList({ ...selectedSkill, slides: newSlides });
    setActiveSlideIndex((prev) =>
      prev >= newSlides.length ? newSlides.length - 1 : prev,
    );
  };

  const moveSlide = (idx: number, direction: "up" | "down") => {
    if (!selectedSkill) return;
    const newSlides = [...selectedSkill.slides];
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= newSlides.length) return;
    const [movedSlide] = newSlides.splice(idx, 1);
    newSlides.splice(targetIdx, 0, movedSlide);
    updateSkillInList({ ...selectedSkill, slides: newSlides });
    setActiveSlideIndex(targetIdx);
  };

  const updateSkillInList = (updated: SkillSlide) => {
    setSelectedSkill(updated);
    setSkills(skills.map((s) => (s.id === updated.id ? updated : s)));
  };

  const updateSlideData = (field: string, value: any) => {
    if (!selectedSkill || activeSlideIndex < 0) return;
    const newSlides = [...selectedSkill.slides];
    if (field === "title") {
      newSlides[activeSlideIndex] = {
        ...newSlides[activeSlideIndex],
        title: value,
      };
    } else {
      const currentData = newSlides[activeSlideIndex].data || {};
      newSlides[activeSlideIndex] = {
        ...newSlides[activeSlideIndex],
        data: { ...currentData, [field]: value },
      };
    }
    updateSkillInList({ ...selectedSkill, slides: newSlides });
  };

  const handleAddQuizItem = () => {
    if (!selectedSkill) return;
    const newItem: BackendQuestion = {
      _id: `q_manual_${Date.now()}`,
      text: "New Verification Question",
      category: "Knowledge Check",
      options: [
        { _id: `o1_${Date.now()}`, text: "Option A", isCorrect: true },
        { _id: `o2_${Date.now()}`, text: "Option B", isCorrect: false },
        { _id: `o3_${Date.now()}`, text: "Option C", isCorrect: false },
        { _id: `o4_${Date.now()}`, text: "Option D", isCorrect: false },
      ],
      explanation: "Educational rationale...",
    };
    updateSkillInList({
      ...selectedSkill,
      quiz: [...(selectedSkill.quiz || []), newItem],
    });
  };

  const renderListEditor = (
    items: string[],
    setter: (newItems: string[]) => void,
    placeholder: string,
  ) => (
    <div className="space-y-3">
      {(items || []).map((item, idx) => (
        <div key={idx} className="flex gap-2 group animate-pop">
          <div
            className={`w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0 font-black text-xs text-slate-300 border border-slate-100`}
          >
            {idx + 1}
          </div>
          <input
            type="text"
            value={item}
            onChange={(e) => {
              const next = [...items];
              next[idx] = e.target.value;
              setter(next);
            }}
            placeholder={placeholder}
            className="flex-1 px-5 py-3 bg-white border border-slate-200 rounded-xl font-bold text-sm outline-none focus:border-medical-500 transition-all"
          />
          <button
            onClick={() => setter(items.filter((_, i) => i !== idx))}
            className="p-3 text-slate-300 hover:text-rose-500 transition-all"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <button
        onClick={() => setter([...(items || []), ""])}
        className="w-full py-3 border-2 border-dashed border-slate-100 rounded-xl text-slate-400 hover:text-medical-600 transition-all text-[10px] font-black tracking-widest flex items-center justify-center gap-2"
      >
        <Plus size={14} /> Add Line
      </button>
    </div>
  );

  const filteredList = skills.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const currentTotalSize = (() => {
    try {
      return JSON.stringify({ skills }).length;
    } catch (e) {
      return 0;
    }
  })();

  return (
    <div className="space-y-8 animate-fade-in pb-24 h-full flex flex-col text-slate-900">
      {showAiModal && (
        <div className="fixed inset-0 z-[150] bg-slate-950/80 backdrop-blur-md flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-pop">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="text-xl font-black text-slate-900 flex items-center gap-3">
                <Sparkles className="text-indigo-500" /> AI Skill Builder
              </h3>
              <button onClick={() => setShowAiModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">
                Procedure Topic
              </label>
              <input
                type="text"
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                placeholder="e.g., Perineal Care for Female Resident"
                className="w-full p-6 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-lg focus:bg-white shadow-inner"
                autoFocus
              />
              <p className="text-[9px] text-slate-400 font-bold px-1">
                "The AI Instructor will generate a full procedural breakdown and
                a corresponding quiz."
              </p>
            </div>
            <div className="p-8 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
              <button
                onClick={() => setShowAiModal(false)}
                className="px-6 py-3 font-black text-[10px] text-slate-400"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateAI}
                disabled={isGenerating || !aiTopic.trim()}
                className="px-10 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs tracking-widest shadow-xl flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50"
              >
                {isGenerating ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Wand2 size={18} />
                )}
                {isGenerating ? "Synthesizing..." : "Build logic"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6 bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl border border-white/5 relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000 pointer-events-none">
          <Hand size={160} />
        </div>
        <div className="relative z-10 flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-3xl font-black text-white tracking-tight flex items-center gap-3">
              <Activity className="text-emerald-400" /> Procedures Lab
            </h2>
            <div className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg flex items-center gap-2">
              <Database size={10} className="text-slate-500" />
              <span className="text-[8px] font-black text-slate-400 tracking-widest">
                Registry:
              </span>
              <span className="text-[9px] font-black text-emerald-400">
                {activeCollection}
              </span>
            </div>
          </div>
          <p className="text-slate-400 font-medium text-sm mt-1">
            Manage procedural checklists and clinical knowledge checks.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <div className="bg-white/5 p-1 rounded-2xl border border-white/10 flex items-center shrink-0">
              <Award className="ml-3 text-indigo-400" size={16} />
              <select
                value={certId}
                onChange={(e) => setCertId(e.target.value)}
                className="bg-transparent text-white px-4 py-2 rounded-xl outline-none font-black text-[10px] cursor-pointer"
              >
                {certs.map((c) => (
                  <option key={c.id} value={c.id} className="text-black">
                    {c.shortName}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleSuggestStrategy}
              disabled={isBlueprinting}
              className="px-6 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] tracking-widest hover:bg-indigo-50 transition-all flex items-center gap-2 shadow-xl border-b-4 border-indigo-900 active:scale-95 disabled:opacity-50"
            >
              {isBlueprinting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Sparkles size={16} />
              )}
              Suggest Procedures
            </button>
            <button
              onClick={() => setShowAiModal(true)}
              className="px-6 py-3 bg-medical-500 text-white rounded-2xl font-black text-[10px] tracking-widest hover:bg-medical-400 transition-all flex items-center gap-2 shadow-xl border-b-4 border-medical-700"
            >
              <Wand2 size={16} /> AI Build Protocol
            </button>

            <div className="flex bg-white/5 p-1 rounded-2xl border border-white/10 gap-1">
              <button
                onClick={handleExport}
                disabled={isExporting || skills.length === 0}
                className="px-4 py-2 text-white hover:bg-white/10 rounded-xl font-black text-[9px] tracking-widest transition-all flex items-center gap-2"
              >
                {isExporting ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Download size={12} />
                )}{" "}
                Export
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                className="px-4 py-2 text-white hover:bg-white/10 rounded-xl font-black text-[9px] tracking-widest transition-all flex items-center gap-2"
              >
                {isImporting ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Upload size={12} />
                )}{" "}
                Ingest
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportFile}
                accept=".json"
                className="hidden"
              />
            </div>

            <button
              onClick={handleSave}
              disabled={isSaving || !selectedSkill}
              className={`px-10 py-4 rounded-2xl font-black text-xs tracking-widest flex items-center gap-2 shadow-2xl transition-all ${isSaving ? "bg-slate-700 opacity-80" : isSaved ? "bg-emerald-600 text-white" : "bg-medical-500 text-white hover:bg-medical-400"} border-b-4 border-black/20`}
            >
              {isSaving ? (
                <Loader2 className="animate-spin" size={20} />
              ) : isSaved ? (
                <CheckCircle size={20} />
              ) : (
                <Save size={20} />
              )}{" "}
              {isSaving
                ? "Syncing..."
                : isSaved
                  ? "Unit Published"
                  : "Save Unit"}
            </button>
          </div>
        </div>
      </div>

      {status && (
        <div
          className={`p-4 rounded-3xl flex items-center justify-between animate-pop border-2 shadow-xl ${status.type === "error" ? "bg-rose-50 border-rose-100 text-rose-700" : status.type === "warning" ? "bg-amber-50 border-amber-100 text-amber-700" : "bg-emerald-50 border-emerald-100 text-emerald-700"}`}
        >
          <div className="flex items-center gap-3 ml-2 min-w-0">
            {status.type === "error" ? (
              <AlertTriangle size={18} className="shrink-0" />
            ) : status.type === "warning" ? (
              <AlertCircle size={18} className="shrink-0" />
            ) : (
              <CheckCircle size={18} className="shrink-0" />
            )}
            <span className="font-black text-xs tracking-widest truncate">
              {status.text}
            </span>
          </div>
          <button
            onClick={() => setStatus(null)}
            className="p-2 hover:bg-black/5 rounded-xl"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 flex-1 min-h-0">
        <div className="xl:col-span-3 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm">
          <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <span className="font-black text-[10px] tracking-widest text-slate-400">
              Procedures
            </span>
            <div className="relative">
              <Search
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300"
                size={12}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-4 pr-8 py-1.5 bg-white border border-slate-200 rounded-lg text-[10px] outline-none w-28 focus:w-40 transition-all shadow-inner"
                placeholder="Filter..."
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-2">
            {isLoading ? (
              <PageLoader message="Hydrating Catalog..." compact />
            ) : filteredList.length === 0 && suggestedTopics.length === 0 ? (
              <div className="py-20 flex flex-col items-center justify-center text-slate-300 opacity-40 gap-4 grayscale scale-90">
                <Layers size={64} strokeWidth={1.5} />
                <p className="font-black text-sm tracking-widest">
                  Registry Vacant
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {suggestedTopics.length > 0 && (
                  <div className="mb-6 space-y-2 animate-slide-up">
                    <p className="px-2 text-[8px] font-black text-indigo-400 tracking-widest">
                      AI Strategy Suggestions
                    </p>
                    {suggestedTopics.map((topic) => (
                      <div
                        key={topic}
                        className="w-full p-2 bg-emerald-50 border-2 border-dashed border-emerald-200 rounded-xl flex items-center justify-between group hover:bg-white hover:border-emerald-500 transition-all"
                      >
                        <span className="truncate text-emerald-800 text-[10px] font-bold px-1">
                          {topic}
                        </span>
                        <div className="flex gap-1 shrink-0">
                          <button
                            onClick={() =>
                              handleProvisionFromTopic(topic, true)
                            }
                            className="p-1.5 bg-emerald-600 text-white rounded-lg shadow-md hover:bg-emerald-700 transition-all"
                            title="Provision + Build with AI"
                          >
                            <Wand2 size={12} />
                          </button>
                          <button
                            onClick={() =>
                              handleProvisionFromTopic(topic, false)
                            }
                            className="p-1.5 bg-white text-emerald-600 rounded-lg border border-emerald-100 shadow-sm hover:bg-emerald-50 transition-all"
                            title="Provision Empty"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {filteredList.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => handleSelectSkill(s)}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer group flex items-center justify-between ${selectedSkill?.id === s.id ? "bg-emerald-50 border-emerald-500 shadow-md" : "bg-white border-transparent hover:bg-slate-50 shadow-sm"}`}
                  >
                    <div className="flex items-center gap-2.5 overflow-hidden flex-1 mr-2">
                      <div
                        className={`p-2 rounded-lg shrink-0 ${selectedSkill?.id === s.id ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400"}`}
                      >
                        {React.createElement(SkillIconResolver(s.icon), {
                          size: 14,
                        })}
                      </div>
                      <p className="font-black text-[10px] text-slate-800 uppercase truncate">
                        {s.name}
                      </p>
                      {Boolean(s.videoUrl || (s as any).hasVideo || s.slides?.some(sl => sl.type === 'embedded' || sl.data?.embedUrl || sl.data?.embedCode || sl.data?.videoUrl || sl.data?.youtubeUrl)) && (
                        <span 
                          className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-[8px] font-black uppercase tracking-wider flex items-center gap-0.5 shrink-0" 
                          title="Clinical Video Demonstration Attached"
                        >
                          <Video size={9} className="text-indigo-600" />
                          Video
                        </span>
                      )}
                      {simulatorScenarios.some(sc => sc.status !== 'archived' && ((s.simulatorScenarioId && sc.id === s.simulatorScenarioId) || (sc.skillId && sc.skillId === s.id))) && (
                        <span 
                          className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[8px] font-black uppercase tracking-wider flex items-center gap-0.5 shrink-0" 
                          title="Interactive Simulation Attached"
                        >
                          <Activity size={9} className="text-blue-600" />
                          Sim
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm("Permanently purge this skill?"))
                          StorageService.deleteSkill(certId, s.id).then(() =>
                            setSkills(skills.filter((x) => x.id !== s.id)),
                          );
                      }}
                      className="p-1.5 text-slate-200 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={handleAddSkill}
              className="w-full py-4 border-2 border-dashed border-slate-100 rounded-2xl text-[10px] font-black uppercase text-slate-400 hover:text-emerald-600 transition-all"
            >
              + Add Skill
            </button>
          </div>
        </div>

        {selectedSkill ? (
          <div className="xl:col-span-9 grid grid-cols-1 lg:grid-cols-4 gap-8 min-h-0 relative">
            {isHydrating && (
              <div className="absolute inset-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center rounded-[2.5rem] animate-fade-in">
                <PageLoader message="Hydrating logic node..." compact />
              </div>
            )}
            <div className="lg:col-span-1 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm">
              <div className="p-4 bg-slate-50 border-b border-slate-100 flex gap-2">
                <button
                  onClick={() => setActiveEditTab("phases")}
                  className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${activeEditTab === "phases" ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:text-slate-700"}`}
                >
                  Phases
                </button>
                <button
                  onClick={() => setActiveEditTab("quiz")}
                  className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${activeEditTab === "quiz" ? "bg-indigo-600 text-white shadow-lg" : "text-slate-400 hover:text-slate-700"}`}
                >
                  Quiz
                </button>
              </div>

              {activeEditTab === "phases" ? (
                <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                  <button
                    onClick={() => setActiveSlideIndex(-1)}
                    className={`w-full p-4 rounded-xl flex items-center gap-3 transition-all text-left ${activeSlideIndex === -1 ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:bg-slate-50"}`}
                  >
                    <Settings2 size={16} />{" "}
                    <span className="font-black text-[9px] uppercase">
                      Identity
                    </span>
                  </button>
                  <div className="h-px bg-slate-100 my-2 mx-2"></div>
                  {selectedSkill.slides &&
                    selectedSkill.slides.map((s, i) => {
                      const config = SLIDE_TYPES.find((t) => t.type === s.type);
                      const IconComponent = config?.icon || PlayCircle;
                      return (
                        <div
                          key={s.id}
                          className="flex items-center gap-1 group"
                        >
                          <button
                            onClick={() => setActiveSlideIndex(i)}
                            className={`flex-1 p-4 rounded-xl flex items-center gap-3 transition-all text-left ${activeSlideIndex === i ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:bg-slate-50"}`}
                          >
                            <div
                              className={`p-1.5 rounded-lg ${activeSlideIndex === i ? "bg-white/20" : "bg-slate-100 text-slate-400"}`}
                            >
                              <IconComponent size={12} />
                            </div>
                            <span className="font-black text-[9px] uppercase truncate flex-1">
                              {config?.label || s.type}
                            </span>
                          </button>
                          <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => moveSlide(i, "up")}
                              disabled={i === 0}
                              className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-0"
                            >
                              <ChevronUp size={14} />
                            </button>
                            <button
                              onClick={() => moveSlide(i, "down")}
                              disabled={
                                i === (selectedSkill.slides?.length || 0) - 1
                              }
                              className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-0"
                            >
                              <ChevronDown size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest px-2">
                      Phases
                    </p>
                    <div className="grid grid-cols-2 gap-1">
                      {SLIDE_TYPES.map((st) => (
                        <button
                          key={st.type}
                          onClick={() => addSlide(st.type)}
                          className="w-full text-left p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-[8px] font-black uppercase text-slate-500 hover:bg-emerald-50 hover:text-emerald-700 transition-all"
                        >
                          {st.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 p-4 space-y-4 overflow-y-auto custom-scrollbar">
                  <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl flex flex-col items-center gap-3 text-center mb-4">
                    <HelpCircle size={24} className="text-indigo-600" />
                    <p className="text-[10px] font-black text-indigo-900 uppercase">
                      Knowledge Check
                    </p>
                    <p className="text-[8px] font-bold text-indigo-400 uppercase">
                      Verification questions for procedural retention.
                    </p>
                  </div>
                  <button
                    onClick={handleAddQuizItem}
                    className="w-full py-4 border-2 border-dashed border-indigo-200 rounded-2xl text-[10px] font-black uppercase text-indigo-600 hover:bg-indigo-50 transition-all"
                  >
                    + Add Question
                  </button>
                  <div className="space-y-2">
                    {selectedSkill.quiz &&
                      selectedSkill.quiz.map((q, qidx) => (
                        <div
                          key={q._id}
                          className="p-4 bg-slate-50 rounded-2xl border border-slate-200 group relative"
                        >
                          <p className="text-[9px] font-black text-slate-900 uppercase truncate pr-6">
                            {q.text}
                          </p>
                          <button
                            onClick={() => {
                              const next = [...(selectedSkill.quiz || [])];
                              next.splice(qidx, 1);
                              updateSkillInList({
                                ...selectedSkill,
                                quiz: next,
                              });
                            }}
                            className="absolute top-4 right-4 p-1 text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-3 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm relative">
              {activeEditTab === "quiz" ? (
                <div className="flex-1 overflow-y-auto p-8 md:p-10 custom-scrollbar space-y-10">
                  <div className="flex justify-between items-center border-b border-slate-100 pb-8">
                    <div>
                      <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                        <Brain className="text-indigo-600" /> Quiz Orchestrator
                      </h3>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                        Verification items for {selectedSkill.name}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {selectedSkill.quiz &&
                      selectedSkill.quiz.map((q, qidx) => (
                        <div
                          key={q._id}
                          className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-6 animate-slide-up shadow-inner relative"
                        >
                          <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                              Question {qidx + 1}
                            </label>
                            <input
                              type="text"
                              value={q.text}
                              onChange={(e) => {
                                const next = [...(selectedSkill.quiz || [])];
                                next[qidx].text = e.target.value;
                                updateSkillInList({
                                  ...selectedSkill,
                                  quiz: next,
                                });
                              }}
                              className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-bold text-sm outline-none focus:border-indigo-500"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {(q.options || []).map((opt, oidx) => (
                              <div
                                key={opt._id}
                                className={`p-4 rounded-2xl border-2 items-center gap-4 transition-all flex ${opt.isCorrect ? "bg-emerald-50 border-emerald-500" : "bg-white border-slate-100"}`}
                              >
                                <button
                                  onClick={() => {
                                    const next = [
                                      ...(selectedSkill.quiz || []),
                                    ];
                                    next[qidx].options = (
                                      next[qidx].options || []
                                    ).map((o, i) => ({
                                      ...o,
                                      isCorrect: i === oidx,
                                    }));
                                    updateSkillInList({
                                      ...selectedSkill,
                                      quiz: next,
                                    });
                                  }}
                                  className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs ${opt.isCorrect ? "bg-emerald-500 text-white" : "bg-white text-slate-300 border border-slate-200 hover:border-emerald-300"}`}
                                >
                                  {opt.isCorrect ? (
                                    <CheckCircle size={14} />
                                  ) : (
                                    String.fromCharCode(65 + oidx)
                                  )}
                                </button>
                                <input
                                  type="text"
                                  value={opt.text}
                                  onChange={(e) => {
                                    const next = [
                                      ...(selectedSkill.quiz || []),
                                    ];
                                    next[qidx].options[oidx].text =
                                      e.target.value;
                                    updateSkillInList({
                                      ...selectedSkill,
                                      quiz: next,
                                    });
                                  }}
                                  className="flex-1 bg-transparent border-none outline-none font-bold text-xs"
                                />
                              </div>
                            ))}
                          </div>

                          <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                              <Brain size={12} className="text-indigo-400" />{" "}
                              Clinical Rationale
                            </label>
                            <textarea
                              value={q.explanation}
                              onChange={(e) => {
                                const next = [...(selectedSkill.quiz || [])];
                                next[qidx].explanation = e.target.value;
                                updateSkillInList({
                                  ...selectedSkill,
                                  quiz: next,
                                });
                              }}
                              className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-medium text-xs italic resize-none h-20 outline-none"
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto p-8 md:p-10 custom-scrollbar">
                  {activeSlideIndex === -1 ? (
                    <div className="space-y-10">
                      <div className="space-y-4">
                        <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                          Protocol Branding
                        </h3>
                        <p className="text-slate-400 text-xs font-medium">
                          Configure the display identity for this clinical
                          skill.
                        </p>
                      </div>
                      <div className="space-y-8">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                            Full Protocol Name
                          </label>
                          <input
                            type="text"
                            value={selectedSkill.name}
                            onChange={(e) =>
                              updateSkillInList({
                                ...selectedSkill,
                                name: e.target.value,
                              })
                            }
                            className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl font-black text-2xl uppercase outline-none focus:bg-white text-slate-800 shadow-inner"
                            placeholder="e.g., Transfer to Wheelchair"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                            System Icon (Lucide)
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={selectedSkill.icon}
                              onChange={(e) =>
                                updateSkillInList({
                                  ...selectedSkill,
                                  icon: e.target.value,
                                })
                              }
                              className="w-full pl-6 pr-14 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-sm outline-none focus:bg-white"
                              placeholder="Hand, Activity, Heart..."
                            />
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white rounded-lg shadow-md border border-slate-100 text-emerald-600">
                              {React.createElement(
                                SkillIconResolver(selectedSkill.icon),
                                { size: 18 },
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Custom Educator AI Upgrade Card */}
                        <div className="p-6 bg-gradient-to-br from-indigo-50 to-emerald-50 dark:from-slate-900/60 dark:to-slate-950/20 rounded-[2rem] border border-indigo-100 dark:border-slate-800 space-y-4 shadow-sm animate-pop text-left">
                          <div className="flex items-center gap-2 text-indigo-750 dark:text-indigo-400">
                            <Sparkles size={18} className="text-indigo-600 animate-pulse" />
                            <h4 className="font-black text-xs uppercase tracking-widest">Enhanced Clinical AI Upgrade</h4>
                          </div>
                          <p className="text-[11px] font-bold text-indigo-950/70 dark:text-slate-350 leading-relaxed">
                            Inject over 20+ years of hospital floor education experience (BSN, MSN, RN, NP, DNP) into this active skill step content. This will automatically fill and upgrade:
                            <span className="block mt-2 space-y-1 font-black text-[10px] text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                              <span>✓ Pearson VUE/Credentia, Prometric & Headmaster Exam focus points</span><br/>
                              <span>✓ Advanced clinical rationales and veteran instructor tips</span><br/>
                              <span>✓ Specific exam board exceptions and auto-fail critical watchouts</span>
                            </span>
                          </p>
                          <button 
                            onClick={handleUpgradeSelectedSkillWithAI}
                            disabled={isGenerating}
                            className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-400 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 border-b-4 border-indigo-800"
                          >
                            {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                            {isGenerating ? 'Upgrading Protocol...' : 'Upgrade with Nurse Educator AI'}
                          </button>
                        </div>

                        {/* Interactive Clinical Simulator Attachment Section */}
                        {(() => {
                          const attachedScenario = simulatorScenarios.find(
                            s => s.status !== 'archived' && (
                              (selectedSkill.simulatorScenarioId && s.id === selectedSkill.simulatorScenarioId) ||
                              (s.skillId && s.skillId === selectedSkill.id)
                            )
                          );

                          return (
                            <div className="p-8 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-6 text-left">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <div className="p-2 bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 rounded-xl border border-blue-200 dark:border-blue-900">
                                      <Activity size={18} />
                                    </div>
                                    <h4 className="text-base font-black text-slate-900 dark:text-white uppercase tracking-tight">
                                      Clinical Simulator Attachment
                                    </h4>
                                  </div>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xl">
                                    Control which procedural skills offer interactive bedside simulation. The <span className="font-bold text-blue-600 dark:text-blue-400">"Simulate"</span> button will only appear for students on skills with an attached scenario.
                                  </p>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => navigate('/app/admin-clinical-simulators')}
                                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 shrink-0 border border-slate-200 dark:border-slate-700"
                                >
                                  <Sliders size={14} />
                                  <span>Clinical Sim Studio</span>
                                  <ExternalLink size={12} className="opacity-60" />
                                </button>
                              </div>

                              {/* Active Attached Scenario Status */}
                              {attachedScenario ? (
                                <div className="p-6 rounded-2xl bg-blue-50/70 dark:bg-blue-950/30 border-2 border-blue-300 dark:border-blue-800 space-y-4">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="px-2.5 py-1 rounded-md bg-emerald-600 text-white text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                                          <CheckCircle2 size={12} /> Active Attached Simulator
                                        </span>
                                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border ${
                                          attachedScenario.status === 'published' 
                                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300' 
                                            : attachedScenario.status === 'testing'
                                            ? 'bg-blue-100 text-blue-800 border-blue-300'
                                            : 'bg-amber-100 text-amber-800 border-amber-300'
                                        }`}>
                                          {attachedScenario.status}
                                        </span>
                                      </div>
                                      <h5 className="text-lg font-black text-slate-900 dark:text-white pt-1">
                                        {attachedScenario.title}
                                      </h5>
                                      <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                                        {attachedScenario.description || 'Step-by-step interactive clinical examination simulation.'}
                                      </p>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                      <button
                                        type="button"
                                        disabled={isAttachingSim}
                                        onClick={handleDetachScenario}
                                        className="px-3.5 py-2 bg-white dark:bg-slate-900 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400 hover:text-rose-700 border border-rose-200 dark:border-rose-900/60 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 shadow-2xs"
                                      >
                                        {isAttachingSim ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                                        <span>Detach Simulator</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => navigate('/app/admin-clinical-simulators')}
                                        className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 shadow-sm"
                                      >
                                        <Play size={13} />
                                        <span>Edit / Test</span>
                                      </button>
                                    </div>
                                  </div>

                                  {/* Quick metadata chips */}
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-blue-200/60 dark:border-blue-900/40 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                    <div className="bg-white/80 dark:bg-slate-900/60 p-2.5 rounded-xl border border-blue-100 dark:border-blue-900/30">
                                      <span className="block text-[9px] uppercase tracking-wider text-slate-400">Environment</span>
                                      <span className="capitalize">{attachedScenario.environment?.replace(/_/g, ' ') || 'Bedside Room'}</span>
                                    </div>
                                    <div className="bg-white/80 dark:bg-slate-900/60 p-2.5 rounded-xl border border-blue-100 dark:border-blue-900/30">
                                      <span className="block text-[9px] uppercase tracking-wider text-slate-400">Stations</span>
                                      <span>{attachedScenario.hotspots?.length || 0} Clinical Zones</span>
                                    </div>
                                    <div className="bg-white/80 dark:bg-slate-900/60 p-2.5 rounded-xl border border-blue-100 dark:border-blue-900/30">
                                      <span className="block text-[9px] uppercase tracking-wider text-slate-400">Auto-Fail Traps</span>
                                      <span>{attachedScenario.instantFailTraps?.length || 0} Board Rules</span>
                                    </div>
                                    <div className="bg-white/80 dark:bg-slate-900/60 p-2.5 rounded-xl border border-blue-100 dark:border-blue-900/30">
                                      <span className="block text-[9px] uppercase tracking-wider text-slate-400">Resident / Patient</span>
                                      <span>{attachedScenario.patient?.name || 'Resident'} ({attachedScenario.patient?.age || '72'}y)</span>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 flex items-start gap-3">
                                  <AlertCircle size={20} className="text-slate-400 shrink-0 mt-0.5" />
                                  <div className="space-y-1">
                                    <p className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                      No Simulator Attached
                                    </p>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                      Students currently see only <strong>Study Mode</strong> and <strong>Test Mode</strong> for this skill. Select an authored simulation scenario below to attach it and enable the "Simulate" button.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Scenarios Selection List */}
                              <div className="space-y-3 pt-2">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                    Available Scenarios in Registry ({simulatorScenarios.length})
                                  </label>
                                  <span className="text-[10px] text-slate-400 font-medium">
                                    Certification: <strong className="text-slate-700 dark:text-slate-200 uppercase">{certId}</strong>
                                  </span>
                                </div>

                                {simulatorScenarios.length === 0 ? (
                                  <div className="p-8 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-center space-y-3">
                                    <p className="text-xs font-bold text-slate-400">
                                      No clinical simulation scenarios found for this certification program.
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => navigate('/app/admin-clinical-simulators')}
                                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all inline-flex items-center gap-2"
                                    >
                                      <Plus size={14} />
                                      <span>Author First Scenario in Studio</span>
                                    </button>
                                  </div>
                                ) : (
                                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                                    {simulatorScenarios.map((sc) => {
                                      const isCurrentlyAttached = attachedScenario?.id === sc.id;
                                      const attachedToOtherSkill = !isCurrentlyAttached && sc.skillId && sc.skillId !== selectedSkill.id;
                                      const otherSkillName = attachedToOtherSkill ? (skills.find(sk => sk.id === sc.skillId)?.name || sc.skillId) : null;

                                      return (
                                        <div
                                          key={sc.id}
                                          className={`p-4 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                                            isCurrentlyAttached 
                                              ? 'bg-blue-50/50 dark:bg-blue-950/40 border-blue-400 dark:border-blue-700' 
                                              : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                                          }`}
                                        >
                                          <div className="space-y-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="font-black text-xs text-slate-800 dark:text-slate-100 truncate">
                                                {sc.title}
                                              </span>
                                              <span className={`px-1.5 py-0.2 rounded text-[8px] font-black uppercase tracking-wider ${
                                                sc.status === 'published' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'
                                              }`}>
                                                {sc.status}
                                              </span>
                                              {isCurrentlyAttached && (
                                                <span className="px-1.5 py-0.2 rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-[8px] font-black uppercase">
                                                  Current
                                                </span>
                                              )}
                                              {attachedToOtherSkill && (
                                                <span className="px-1.5 py-0.2 rounded bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 text-[8px] font-bold">
                                                  Linked to: {otherSkillName}
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-1">
                                              {sc.hotspots?.length || 0} Stations · {sc.instantFailTraps?.length || 0} Auto-Fail Traps · Resident: {sc.patient?.name || 'Resident'}
                                            </p>
                                          </div>

                                          <div className="flex items-center gap-2 shrink-0">
                                            {isCurrentlyAttached ? (
                                              <button
                                                type="button"
                                                disabled={isAttachingSim}
                                                onClick={handleDetachScenario}
                                                className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all"
                                              >
                                                Detach
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                disabled={isAttachingSim}
                                                onClick={() => handleAttachScenario(sc)}
                                                className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[9px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-2xs"
                                              >
                                                {isAttachingSim ? <Loader2 size={11} className="animate-spin" /> : <Activity size={11} />}
                                                <span>{attachedToOtherSkill ? 'Reassign to this Skill' : 'Attach to this Skill'}</span>
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (selectedSkill.slides && selectedSkill.slides[activeSlideIndex]) ? (
                    <div className="space-y-10 animate-fade-in">
                      <div className="flex justify-between items-center border-b border-slate-100 pb-8">
                        <div className="flex items-center gap-4">
                          <div
                            className={`p-3 text-white rounded-2xl shadow-lg ${SLIDE_TYPES.find((t) => t.type === selectedSkill.slides[activeSlideIndex]?.type)?.color || "bg-slate-900"}`}
                          >
                            <Layers size={20} />
                          </div>
                          <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                            {selectedSkill.slides[activeSlideIndex]?.type} Matrix
                          </h3>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleFillPhaseWithAI}
                            disabled={isGeneratingPhase}
                            className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg active:scale-95 disabled:opacity-30"
                          >
                            {isGeneratingPhase ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <Wand2 size={12} />
                            )}
                            {isGeneratingPhase
                              ? "Synthesizing..."
                              : "Fill with AI"}
                          </button>
                          <button
                            onClick={() => removeSlide(activeSlideIndex)}
                            className="p-2 text-slate-300 hover:text-rose-500 transition-all"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>

                      {(selectedSkill.slides[activeSlideIndex].type ===
                        "infographic" ||
                        selectedSkill.slides[activeSlideIndex].type ===
                          "procedure") && (
                        <div className="space-y-10">
                          <div className="p-8 bg-slate-900 rounded-[2.5rem] text-white space-y-6 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:rotate-12 transition-transform duration-700">
                              <Wand2 size={80} />
                            </div>
                            <div className="relative z-10">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="text-[10px] font-black text-medical-400 uppercase tracking-widest">
                                  Visual Ingestion
                                </h4>
                                {selectedSkill.slides[activeSlideIndex].type ===
                                  "infographic" && (
                                  <button
                                    onClick={handleSynthesizeVisualLogic}
                                    disabled={isSynthesizingLogic}
                                    className="text-[9px] font-black uppercase text-indigo-400 hover:text-white flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10"
                                  >
                                    {isSynthesizingLogic ? (
                                      <Loader2
                                        size={12}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <Brain size={12} />
                                    )}
                                    AI Blueprint
                                  </button>
                                )}
                              </div>
                              <div className="space-y-4">
                                <textarea
                                  placeholder="Describe clinical situation for AI generator..."
                                  value={
                                    selectedSkill.slides[activeSlideIndex].data
                                      ?.visualAlt || ""
                                  }
                                  onChange={(e) =>
                                    updateSlideData("visualAlt", e.target.value)
                                  }
                                  className="w-full p-5 bg-white/5 border border-white/10 rounded-2xl font-bold text-sm outline-none focus:bg-white/10 h-24 shadow-inner"
                                />
                                <div className="flex items-center gap-2">
                                  <LinkIcon
                                    className="text-indigo-400 shrink-0"
                                    size={14}
                                  />
                                  <input
                                    type="text"
                                    value={
                                      selectedSkill.slides[activeSlideIndex]
                                        .data?.imageUrl || ""
                                    }
                                    onChange={(e) =>
                                      updateSlideData(
                                        "imageUrl",
                                        e.target.value,
                                      )
                                    }
                                    className="flex-1 bg-white/5 border border-white/10 rounded-xl p-2.5 font-mono text-[9px] outline-none"
                                    placeholder="Paste manual asset URL..."
                                  />
                                </div>
                              </div>
                              <button
                                onClick={handleGenerateVisualAnchor}
                                disabled={
                                  isGeneratingVisual ||
                                  !selectedSkill.slides[activeSlideIndex].data
                                    ?.visualAlt
                                }
                                className="mt-6 w-full py-4 bg-medical-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50"
                              >
                                {isGeneratingVisual ? (
                                  <Loader2 className="animate-spin" />
                                ) : (
                                  <Sparkles size={16} />
                                )}
                                {isGeneratingVisual
                                  ? "Illustrating..."
                                  : "Synthesize AI Illustration"}
                              </button>
                            </div>
                          </div>

                          {selectedSkill.slides[activeSlideIndex].data
                            ?.imageUrl && (
                            <div className="rounded-[2.5rem] overflow-hidden border-4 border-slate-100 shadow-xl group relative aspect-video bg-slate-900">
                              <img
                                src={
                                  selectedSkill.slides[activeSlideIndex].data
                                    .imageUrl
                                }
                                className="w-full h-full object-contain"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button
                                  onClick={() =>
                                    updateSlideData("imageUrl", "")
                                  }
                                  className="p-4 bg-rose-600 text-white rounded-full shadow-2xl transition-all active:scale-95"
                                >
                                  <Trash2 size={24} />
                                </button>
                              </div>
                            </div>
                          )}

                          {selectedSkill.slides[activeSlideIndex].type ===
                            "infographic" && (
                            <div className="space-y-4">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                Learning Checkpoints
                              </label>
                              {renderListEditor(
                                selectedSkill.slides[activeSlideIndex].data
                                  ?.observations || [],
                                (n) => updateSlideData("observations", n),
                                "What should students notice in this step?",
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {selectedSkill.slides[activeSlideIndex].type ===
                        "overview" && (
                        <div className="space-y-8">
                          {[
                            {
                              id: "importance",
                              label: "Clinical Significance",
                              icon: Info,
                            },
                            {
                              id: "examContext",
                              label: "Board Registry Context",
                              icon: Target,
                            },
                            {
                              id: "safetyFocus",
                              label: "Mandatory Safety Point",
                              icon: ShieldAlert,
                            },
                          ].map((field) => (
                            <div key={field.id} className="space-y-3">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <field.icon
                                  size={14}
                                  className="text-indigo-500"
                                />{" "}
                                {field.label}
                              </label>
                              <textarea
                                value={
                                  selectedSkill.slides[activeSlideIndex].data?.[
                                    field.id
                                  ] || ""
                                }
                                onChange={(e) =>
                                  updateSlideData(field.id, e.target.value)
                                }
                                className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[100px] font-medium text-slate-800 outline-none focus:bg-white resize-none shadow-inner"
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedSkill.slides[activeSlideIndex].type ===
                        "procedure" && (
                        <div className="space-y-8">
                          <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                              Step Identification
                            </label>
                            <input
                              type="text"
                              value={
                                selectedSkill.slides[activeSlideIndex].title ||
                                ""
                              }
                              onChange={(e) => {
                                const n = [...selectedSkill.slides];
                                n[activeSlideIndex].title = e.target.value;
                                updateSkillInList({
                                  ...selectedSkill,
                                  slides: n,
                                });
                              }}
                              className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-black uppercase text-xs outline-none focus:bg-white"
                              placeholder="e.g., Preparation Phase"
                            />
                          </div>
                          <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                              Registry Protocol Checklist
                            </label>
                            {renderListEditor(
                              selectedSkill.slides[activeSlideIndex].data
                                ?.steps || [],
                              (n) => updateSlideData("steps", n),
                              "Exact clinical action step...",
                            )}
                          </div>
                        </div>
                      )}

                      {selectedSkill.slides[activeSlideIndex].type ===
                        "supplies" && (
                        <div className="space-y-6">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                            Clinical Inventory List
                          </label>
                          {renderListEditor(
                            selectedSkill.slides[activeSlideIndex].data
                              ?.items || [],
                            (n) => updateSlideData("items", n),
                            "Item/Supplies name...",
                          )}
                        </div>
                      )}

                      {selectedSkill.slides[activeSlideIndex].type ===
                        "criticals" && (
                        <div className="space-y-6">
                          <div className="p-6 bg-rose-50 border-2 border-rose-100 rounded-3xl flex items-center gap-4 shadow-sm">
                            <div className="p-2 bg-rose-600 text-white rounded-lg shadow-md">
                              <AlertTriangle size={18} />
                            </div>
                            <p className="text-[10px] font-black text-rose-900 uppercase leading-relaxed">
                              Safety Protocol: Failure to execute these results
                              in DISQUALIFICATION.
                            </p>
                          </div>
                          {renderListEditor(
                            selectedSkill.slides[activeSlideIndex].data
                              ?.items || [],
                            (n) => updateSlideData("items", n),
                            "Critical failure trigger...",
                          )}
                        </div>
                      )}

                      {selectedSkill.slides[activeSlideIndex].type ===
                        "reporting" && (
                        <div className="space-y-8">
                          <div className="space-y-6">
                            <div className="flex items-center gap-4">
                              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                                <MessageSquare size={28} />
                              </div>
                              <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                                Reporting Protocol
                              </h2>
                            </div>
                            <div className="p-10 bg-slate-50 rounded-[2.5rem] border border-slate-100 shadow-inner">
                              <textarea
                                value={
                                  selectedSkill.slides[activeSlideIndex].data
                                    ?.instruction || ""
                                }
                                onChange={(e) =>
                                  updateSlideData("instruction", e.target.value)
                                }
                                className="w-full bg-transparent border-none outline-none font-bold text-slate-600 leading-relaxed italic h-24 resize-none"
                                placeholder="Guidance for reporting observations..."
                              />
                            </div>
                          </div>
                          <div className="space-y-6">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                              <Target size={14} className="text-indigo-500" />{" "}
                              Sample Registry Wording
                            </h4>
                            <div className="p-10 rounded-[2.5rem] border-2 bg-slate-900 text-emerald-400 shadow-2xl">
                              <textarea
                                value={
                                  selectedSkill.slides[activeSlideIndex].data
                                    ?.sampleWording || ""
                                }
                                onChange={(e) =>
                                  updateSlideData(
                                    "sampleWording",
                                    e.target.value,
                                  )
                                }
                                className="w-full bg-transparent border-none outline-none font-black uppercase tracking-tighter leading-tight italic text-xl md:text-3xl resize-none h-32"
                                placeholder="EXACT WORDING SCRIPT..."
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedSkill.slides[activeSlideIndex].type ===
                        "completion" && (
                        <div className="space-y-6">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                            Success Criteria List
                          </label>
                          {renderListEditor(
                            selectedSkill.slides[activeSlideIndex].data
                              ?.items || [],
                            (n) => updateSlideData("items", n),
                            "Successful completion criterion...",
                          )}
                        </div>
                      )}

                      {selectedSkill.slides[activeSlideIndex].type ===
                        "embedded" && (
                        <div className="space-y-8 animate-fade-in">
                          <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                              <FileText size={14} className="text-indigo-500" />{" "}
                              Embedded Phase Title
                            </label>
                            <input
                              type="text"
                              value={
                                selectedSkill.slides[activeSlideIndex].title ||
                                ""
                              }
                              onChange={(e) =>
                                updateSlideData("title", e.target.value)
                              }
                              className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-black text-xs outline-none focus:bg-white"
                              placeholder="e.g., Google Slides Chapter 1, Manual Content Phase"
                            />
                          </div>

                          <div className="space-y-4">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                              <Settings size={14} className="text-indigo-500" />{" "}
                              Embedding Option / Mode
                            </label>
                            <div className="flex gap-4">
                              <button
                                type="button"
                                onClick={() =>
                                  updateSlideData("embedMode", "url")
                                }
                                className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black tracking-widest uppercase transition-all flex items-center justify-center gap-2 ${
                                  selectedSkill.slides[activeSlideIndex].data
                                    ?.embedMode === "url"
                                    ? "bg-indigo-600 text-white shadow-md"
                                    : "bg-slate-50 border border-slate-100 text-slate-600 hover:bg-slate-100"
                                }`}
                              >
                                <LinkIcon size={12} /> Option 1: External URL
                                Iframe
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  updateSlideData("embedMode", "code")
                                }
                                className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black tracking-widest uppercase transition-all flex items-center justify-center gap-2 ${
                                  selectedSkill.slides[activeSlideIndex].data
                                    ?.embedMode === "code"
                                    ? "bg-indigo-600 text-white shadow-md"
                                    : "bg-slate-50 border border-slate-100 text-slate-600 hover:bg-slate-100"
                                }`}
                              >
                                <FileText size={12} /> Option 3: Raw Embedded
                                HTML Code
                              </button>
                            </div>
                          </div>

                          {selectedSkill.slides[activeSlideIndex].data
                            ?.embedMode === "url" ? (
                            <div className="space-y-4 animate-fade-in">
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                    <LinkIcon
                                      size={14}
                                      className="text-indigo-500"
                                    />{" "}
                                    External Resource URL
                                  </label>
                                  <button
                                    type="button"
                                    disabled={isExtractingVideoMeta || !selectedSkill.slides[activeSlideIndex].data?.embedUrl}
                                    onClick={() => handleExtractVideoMetadata()}
                                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 transition-all disabled:opacity-40 cursor-pointer"
                                  >
                                    {isExtractingVideoMeta ? (
                                      <Loader2 size={12} className="animate-spin text-indigo-600" />
                                    ) : (
                                      <Sparkles size={12} className="text-indigo-600" />
                                    )}
                                    <span>{isExtractingVideoMeta ? "Extracting..." : "Auto-Extract Video Details"}</span>
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={
                                    selectedSkill.slides[activeSlideIndex].data
                                      ?.embedUrl || ""
                                  }
                                  onChange={(e) => {
                                    updateSlideData("embedUrl", e.target.value);
                                  }}
                                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-mono text-xs outline-none focus:bg-white"
                                  placeholder="Paste clean website, slides presentation, or YouTube link here (e.g. https://www.youtube.com/watch?v=...)"
                                />
                              </div>

                              {metaNotice && (
                                <div className="p-3 bg-indigo-50/80 border border-indigo-200/80 rounded-xl text-indigo-900 text-xs font-semibold flex items-center gap-2">
                                  <Info size={14} className="text-indigo-600 shrink-0" />
                                  <span>{metaNotice}</span>
                                </div>
                              )}

                              {/* Video Details Card if extracted */}
                              {selectedSkill.slides[activeSlideIndex].data?.videoTitle && (
                                <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-in">
                                  {selectedSkill.slides[activeSlideIndex].data?.videoThumbnail && (
                                    <img 
                                      src={selectedSkill.slides[activeSlideIndex].data.videoThumbnail} 
                                      alt="Video thumbnail"
                                      className="w-24 h-16 object-cover rounded-xl border border-slate-200 shadow-sm shrink-0"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[9px] font-black uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200/60">Clinical Demonstration</span>
                                      <span className="text-slate-300">·</span>
                                      <span className="text-[10px] text-slate-700 font-bold truncate">
                                        Demonstration By: <span className="font-black text-slate-900">{selectedSkill.slides[activeSlideIndex].data?.videoAuthor || "Clinical Faculty"}</span>
                                      </span>
                                    </div>
                                    <h5 className="text-xs font-bold text-slate-900 truncate">
                                      {selectedSkill.slides[activeSlideIndex].data.videoTitle}
                                    </h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => updateSlideData("title", selectedSkill.slides[activeSlideIndex].data.videoTitle)}
                                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[10px] font-black tracking-wide uppercase transition-all shadow-sm shrink-0"
                                  >
                                    Apply as Title
                                  </button>
                                </div>
                              )}

                              {(() => {
                                const currentUrl = selectedSkill.slides[activeSlideIndex].data?.embedUrl || "";
                                const details = inspectEmbedUrl(currentUrl);
                                if (details.isBlockedRoot) {
                                  return (
                                    <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs font-semibold">
                                      <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                      <div>
                                        <p className="font-bold">Specific Video Link Required</p>
                                        <p className="text-[11px] text-amber-700 mt-0.5">
                                          The YouTube home page (<code>https://www.youtube.com/</code>) sets <code>X-Frame-Options: SAMEORIGIN</code> and cannot be embedded in an iframe.
                                          Please paste a specific video URL (e.g. <code>https://www.youtube.com/watch?v=...</code> or <code>https://youtu.be/...</code>).
                                        </p>
                                      </div>
                                    </div>
                                  );
                                }
                                if (details.provider === 'youtube' && details.embedUrl) {
                                  return (
                                    <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-[10px] font-bold">
                                      <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                                      <span>YouTube video configured for embed playback without X-Frame-Options blocking.</span>
                                    </div>
                                  );
                                }
                                return null;
                              })()}

                              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                                <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">
                                  Supports YouTube links, Vimeo, Google Slides, or clean https:// URLs.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const sample = "https://www.youtube.com/watch?v=3PmVJQUCm4E";
                                    updateSlideData("embedUrl", sample);
                                    handleExtractVideoMetadata(sample);
                                  }}
                                  className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 underline decoration-dotted cursor-pointer"
                                >
                                  Insert Sample Clinical Video
                                </button>
                              </div>

                              {/* Responsive Aspect Ratio Selector */}
                              <div className="space-y-3 pt-3 border-t border-slate-100">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                  <Sliders size={14} className="text-indigo-500" /> Frame Aspect Ratio & Viewport Scaling
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                  {[
                                    { id: '16:9', label: '16:9 Widescreen', desc: 'Standard YouTube / Vimeo' },
                                    { id: '4:3', label: '4:3 Presentation', desc: 'Classic Slides / Docs' },
                                    { id: 'custom', label: 'Custom', desc: 'Explicit px / %' }
                                  ].map(ratio => {
                                    const current = selectedSkill.slides[activeSlideIndex].data?.aspectRatio || '16:9';
                                    const isSelected = current === ratio.id;
                                    return (
                                      <button
                                        key={ratio.id}
                                        type="button"
                                        onClick={() => updateSlideData('aspectRatio', ratio.id)}
                                        className={`p-3 rounded-xl text-left border transition-all ${
                                          isSelected 
                                            ? 'bg-indigo-50 border-indigo-300 text-indigo-950 shadow-sm' 
                                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                        }`}
                                      >
                                        <div className="text-xs font-bold">{ratio.label}</div>
                                        <div className="text-[9px] text-slate-500 mt-0.5">{ratio.desc}</div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Custom Width & Height only if custom aspect ratio chosen */}
                              {selectedSkill.slides[activeSlideIndex].data?.aspectRatio === 'custom' ? (
                                <div className="grid grid-cols-2 gap-4 pt-2">
                                  <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Frame Width</label>
                                    <input
                                      type="text"
                                      value={selectedSkill.slides[activeSlideIndex].data?.embedWidth || "100%"}
                                      onChange={(e) => updateSlideData("embedWidth", e.target.value)}
                                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-mono text-xs outline-none focus:bg-white"
                                      placeholder="e.g. 100% or 800px"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Frame Height</label>
                                    <input
                                      type="text"
                                      value={selectedSkill.slides[activeSlideIndex].data?.embedHeight || "500px"}
                                      onChange={(e) => updateSlideData("embedHeight", e.target.value)}
                                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-mono text-xs outline-none focus:bg-white"
                                      placeholder="e.g. 500px"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-[10px] text-slate-500 flex items-center gap-2">
                                  <CheckCircle2 size={13} className="text-indigo-500 shrink-0" />
                                  <span>Responsive 16:9 container automatically scales to 100% width on phones, tablets, and widescreen desktop monitors without letterbox bars.</span>
                                </div>
                              )}

                              {/* Clinical Focus / Video Learning Notes */}
                              <div className="space-y-2 pt-3 border-t border-slate-100">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                    <ClipboardList size={14} className="text-indigo-500" /> Key Clinical Focus & Learning Objectives
                                  </label>
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const title = selectedSkill.slides[activeSlideIndex].data?.videoTitle || selectedSkill.slides[activeSlideIndex].title || selectedSkill.name;
                                      try {
                                        const notes = await generateAISkillProtocol(selectedSkill.name, title);
                                        if (notes && notes.length > 0) {
                                          updateSlideData('videoNotes', notes.slice(0, 4).join('\n• '));
                                        }
                                      } catch (e) {}
                                    }}
                                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer"
                                  >
                                    <Sparkles size={12} /> Auto-Draft Clinical Takeaways
                                  </button>
                                </div>
                                <textarea
                                  value={selectedSkill.slides[activeSlideIndex].data?.videoNotes || ""}
                                  onChange={(e) => updateSlideData("videoNotes", e.target.value)}
                                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs outline-none focus:bg-white resize-none"
                                  rows={3}
                                  placeholder="Add key test reminders or clinical rationale for students to study alongside this video..."
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3 animate-fade-in">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <FileText
                                  size={14}
                                  className="text-indigo-500"
                                />{" "}
                                Raw HTML / Embed Code Snippet
                              </label>
                              <textarea
                                value={
                                  selectedSkill.slides[activeSlideIndex].data
                                    ?.embedCode || ""
                                }
                                onChange={(e) =>
                                  updateSlideData("embedCode", e.target.value)
                                }
                                className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[160px] font-mono text-xs outline-none focus:bg-white resize-none shadow-inner"
                                placeholder="Paste your HTML iframe embed code (e.g. <iframe src='...'></iframe>) or any custom HTML widgets..."
                              />
                              <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mt-1">
                                Make sure script tags match sandbox policies for
                                safe execution.
                              </p>
                            </div>
                          )}

                          {/* Live Preview Inside Editor */}
                          <div className="space-y-4 pt-6 border-t border-slate-100">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                              Live Embed Frame Preview
                            </label>
                            <div className="w-full rounded-[2rem] overflow-hidden border border-slate-200 bg-slate-50 p-4 min-h-[300px] flex flex-col items-center justify-center relative col-span-2">
                              {selectedSkill.slides[activeSlideIndex].data?.embedMode === "url" || selectedSkill.slides[activeSlideIndex].data?.embedMode === "code" ? (
                                <EmbeddedPhasePlayer 
                                  embedMode={selectedSkill.slides[activeSlideIndex].data?.embedMode}
                                  embedUrl={selectedSkill.slides[activeSlideIndex].data?.embedUrl}
                                  embedCode={selectedSkill.slides[activeSlideIndex].data?.embedCode}
                                  aspectRatio={selectedSkill.slides[activeSlideIndex].data?.aspectRatio || '16:9'}
                                  embedWidth={selectedSkill.slides[activeSlideIndex].data?.embedWidth}
                                  embedHeight={selectedSkill.slides[activeSlideIndex].data?.embedHeight}
                                  title={selectedSkill.slides[activeSlideIndex].title}
                                  videoTitle={selectedSkill.slides[activeSlideIndex].data?.videoTitle}
                                  videoAuthor={selectedSkill.slides[activeSlideIndex].data?.videoAuthor}
                                  videoAuthorUrl={selectedSkill.slides[activeSlideIndex].data?.videoAuthorUrl}
                                  videoThumbnail={selectedSkill.slides[activeSlideIndex].data?.videoThumbnail}
                                  videoNotes={selectedSkill.slides[activeSlideIndex].data?.videoNotes}
                                  videoDescription={selectedSkill.slides[activeSlideIndex].data?.videoDescription}
                                />
                              ) : (
                                <div className="text-center text-slate-400 space-y-2 py-10">
                                  <LinkIcon
                                    size={32}
                                    className="mx-auto opacity-30 animate-pulse"
                                  />
                                  <p className="text-[10px] font-black uppercase tracking-widest">
                                    Awaiting valid embed resource...
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-12 text-center text-slate-400 space-y-3">
                      <p className="font-black text-xs uppercase tracking-widest">
                        Phase Not Selected
                      </p>
                      <p className="text-[11px] text-slate-400">
                        Select a phase from the list or return to Identity.
                      </p>
                      <button
                        type="button"
                        onClick={() => setActiveSlideIndex(-1)}
                        className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-wider"
                      >
                        Return to Identity
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="xl:col-span-9 flex flex-col items-center justify-center bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 opacity-60">
            <div className="w-24 h-24 bg-white rounded-[2.5rem] flex items-center justify-center text-slate-200 shadow-xl mb-6">
              <Hand size={48} />
            </div>
            <p className="font-black text-slate-400 uppercase tracking-[0.3em] text-sm text-center">
              Protocol Registry Vacant.
              <br />
              Select a procedure to modify or add one.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ToggleLeft = ({ size }: { size: number }) => (
  <svg width={size} height={size / 2} viewBox="0 0 48 24" fill="none">
    <rect
      width="48"
      height="24"
      rx="12"
      fill="currentColor"
      fillOpacity="0.1"
    />
    <circle cx="12" cy="12" r="8" fill="white" shadow-lg="true" />
  </svg>
);

const ToggleRight = ({ size }: { size: number }) => (
  <svg width={size} height={size / 2} viewBox="0 0 48 24" fill="none">
    <rect width="48" height="24" rx="12" fill="currentColor" />
    <circle cx="36" cy="12" r="8" fill="white" />
  </svg>
);
